import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Play, Newspaper } from "lucide-react";
import useActualitesParams from "../hooks/useActualitesParams";

// ✅ Image placeholder en base64 (SVG) - fonctionne offline
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='1920' height='1080' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='48' fill='%239ca3af'%3EImage non disponible%3C/text%3E%3C/svg%3E";

const NotreEcole = () => {
  const { actualites, loading, error } = useActualitesParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState(1);

  // Filtrer uniquement les actualités actives
  const activeActualites = actualites.filter(a => a.actif);

  // Gestion de l'autoplay
  useEffect(() => {
    if (!isPlaying || activeActualites.length === 0) return;
    
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev === activeActualites.length - 1 ? 0 : prev + 1));
    }, 7000);
    
    return () => clearInterval(timer);
  }, [isPlaying, activeActualites.length]);

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="w-full mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-6 h-6 text-[#246990]" />
          <h2 className="text-lg font-bold text-[#246990]">Dernières actualités</h2>
        </div>
        <div className="w-full h-[calc(100vh-150px)] md:h-[calc(100vh-180px)] rounded-2xl shadow-md bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#246990] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement des actualités...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="w-full mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-6 h-6 text-[#246990]" />
          <h2 className="text-lg font-bold text-[#246990]">Dernières actualités</h2>
        </div>
        <div className="w-full h-[calc(100vh-150px)] md:h-[calc(100vh-180px)] rounded-2xl shadow-md bg-red-50 flex items-center justify-center border-2 border-red-200">
          <div className="text-center text-red-600">
            <p className="font-semibold mb-2">Erreur de chargement</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage si aucune actualité active
  if (activeActualites.length === 0) {
    return (
      <div className="w-full mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-6 h-6 text-[#246990]" />
          <h2 className="text-lg font-bold text-[#246990]">Dernières actualités</h2>
        </div>
        <div className="w-full h-[calc(100vh-150px)] md:h-[calc(100vh-180px)] rounded-2xl shadow-md bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <Newspaper size={64} className="mx-auto mb-4 text-gray-300" />
            <p className="font-medium text-lg mb-2">Aucune actualité disponible</p>
            <p className="text-sm">Les actualités seront bientôt publiées ici.</p>
          </div>
        </div>
      </div>
    );
  }

  const safeIndex = Math.min(currentIndex, activeActualites.length - 1);
  const currentActualite = activeActualites[safeIndex];

  return (
    <div className="w-full mb-8">
      {/* 🔹 TITRE DE LA SECTION */}
      <div className="flex items-center gap-2 mb-4">
        <Newspaper className="w-6 h-6 text-[#246990]" />
        <h2 className="text-lg font-bold text-[#246990]">Dernières actualités</h2>
      </div>

      {/* 🔹 DIAPORAMA ÉPURÉ */}
      <section className="relative w-full h-[calc(100vh-150px)] md:h-[calc(100vh-180px)] overflow-hidden rounded-2xl shadow-2xl bg-gray-900">
        
        {/* Image avec animation fluide */}
        <div className="absolute inset-0 overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.img
              key={currentActualite.id}
              src={`${currentActualite.image_url}?t=${Date.now()}`}
              alt={currentActualite.titre}
              custom={direction}
              initial={{ x: direction > 0 ? '100%' : '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction > 0 ? '-100%' : '100%', opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.src = PLACEHOLDER_IMAGE;
              }}
            />
          </AnimatePresence>
          
          {/* Overlay subtil pour améliorer la lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 pointer-events-none" />
        </div>

        {/* Bouton Play/Pause minimaliste */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2.5 bg-white/90 hover:bg-white rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-110 group"
            aria-label={isPlaying ? "Pause" : "Lire"}
          >
            {isPlaying ? (
              <Pause size={18} className="text-[#246990] group-hover:text-[#1a4d6e]" />
            ) : (
              <Play size={18} className="text-[#246990] group-hover:text-[#1a4d6e]" />
            )}
          </button>
        </div>

        {/* Légende épurée avec couleur unie */}
        <div
          className="absolute bottom-0 left-0 right-0 z-10 px-6 py-4 md:px-8 md:py-5"
          style={{
            backgroundColor: currentActualite.couleur_fond || '#246990',
          }}
        >
          <h3 
            className="text-lg md:text-xl font-semibold mb-1 drop-shadow-sm"
            style={{ 
              color: currentActualite.couleur_texte || '#ffffff' 
            }}
          >
            {currentActualite.titre}
          </h3>
          {currentActualite.description && (
            <p 
              className="text-sm md:text-base drop-shadow-sm"
              style={{ 
                color: currentActualite.couleur_texte || '#ffffff',
                opacity: 0.9
              }}
            >
              {currentActualite.description}
            </p>
          )}
        </div>

        {/* Indicateurs de pagination minimalistes */}
        {activeActualites.length > 1 && (
          <div className="absolute bottom-6 right-6 flex gap-2 z-20">
            {activeActualites.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentIndex(i);
                  setDirection(i > safeIndex ? 1 : -1);
                }}
                className={`transition-all duration-300 rounded-full ${
                  i === safeIndex 
                    ? "w-7 h-2.5 bg-white shadow-md" 
                    : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Aller à l'actualité ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default NotreEcole;